import type { AudienceCreationFlowSnapshot } from '@digital-twins/shared-types'
import i18n from '@/i18n'
import type {
  ImportStepDefinition,
  ImportStepKey,
  ImportStepStatus,
} from '@/types/components/audiences/import-shell'

export {
  getStepRouteFallback,
  isWizardInternalRoute,
  stepKeyFromPathname,
  stepKeyFromRoute,
} from './import-wizard-routes'

function importStepLabel(
  key: Exclude<ImportStepKey, 'creating'>,
): string {
  return String(i18n.t(`audienceCreate.steps.${key}`, { ns: 'app' }))
}

/**
 * Derive the visible step definitions from a session snapshot.
 *
 * Steps are included/excluded based on branch summaries:
 * - Sheets:  only when multi-sheet file requires sheet assignment
 * - Enrich:  always shown when enabled (skipped renders as completed)
 * - Core Data, Demographics, Audience Review: always present
 *
 * For CSV (no sheets, context skipped): 4 steps (Core Data → Enrich ✓ → Demographics → Audience Review)
 * For XLSX multi-sheet with context:    5 steps (Core Data → Sheets → Enrich → Demographics → Audience Review)
 */
export function buildImportSteps(
  snapshot: AudienceCreationFlowSnapshot,
  currentStep: ImportStepKey,
  options?: { contextMaterialsStepEnabled?: boolean },
): ImportStepDefinition[] {
  const { branchSummaries, links } = snapshot
  const preflightPending = snapshot.preflightStatus === 'pending'
  /** Dataset preflight webhook not landed yet — demographics/segmentation cannot be "done" in the rail. */
  const identityCompleted =
    !preflightPending && branchSummaries.identity.state === 'satisfied'
  const segmentationDone =
    !preflightPending &&
    (branchSummaries.preview.readyToCreate === true ||
      branchSummaries.preview.state === 'eligible')
  const steps: ImportStepDefinition[] = []

  // Step 1 = Upload dataset; sheet selection is part of this step (no separate "Sheets" step).
  const uploadDone =
    branchSummaries.upload.state !== 'workbook_assignment_required' &&
    branchSummaries.upload.state !== 'empty'
  const uploadActive =
    currentStep === 'upload' || currentStep === 'workbook'
  steps.push({
    key: 'upload',
    label: importStepLabel('upload'),
    status: uploadDone ? 'completed' : uploadActive ? 'active' : 'pending',
    route: '/audiences/new',
  })

  const ctxState = branchSummaries.contextMaterials.state
  const ctxEnabled = options?.contextMaterialsStepEnabled !== false
  if (ctxEnabled) {
    steps.push({
      key: 'context',
      label: importStepLabel('context'),
      status: resolveStatus('context', currentStep, ctxState === 'completed' || ctxState === 'skipped'),
      route: links.contextMaterials,
    })
  }

  steps.push({
    key: 'identity',
    label: importStepLabel('identity'),
    status: resolveStatus(
      'identity',
      currentStep,
      identityCompleted,
    ),
    route: links.requiredFields,
  })

  steps.push({
    key: 'segmentation',
    label: importStepLabel('segmentation'),
    status: resolveStatus('segmentation', currentStep, segmentationDone),
    route: links.segmentation,
  })

  const confirmEligible = branchSummaries.create.state === 'eligible'
  steps.push({
    key: 'confirm',
    label: importStepLabel('confirm'),
    status: resolveStatus('confirm', currentStep, confirmEligible),
  })

  return steps
}

export function buildNewAudienceEntrySteps(): ImportStepDefinition[] {
  return [
    {
      key: 'upload',
      label: importStepLabel('upload'),
      status: 'active',
      route: '/audiences/new',
    },
    {
      key: 'context',
      label: importStepLabel('context'),
      status: 'pending',
    },
    {
      key: 'identity',
      label: importStepLabel('identity'),
      status: 'pending',
    },
    {
      key: 'segmentation',
      label: importStepLabel('segmentation'),
      status: 'pending',
    },
    {
      key: 'confirm',
      label: importStepLabel('confirm'),
      status: 'pending',
    },
  ]
}

/** Wizard step order for "completed only if before current" rule. */
const WIZARD_STEP_ORDER: ImportStepKey[] = [
  'upload',
  'context',
  'identity',
  'segmentation',
  'confirm',
]

export function stepOrderIndex(stepKey: ImportStepKey): number {
  const key = stepKey === 'workbook' ? 'upload' : stepKey
  const idx = WIZARD_STEP_ORDER.indexOf(key)
  return idx === -1 ? 999 : idx
}

function resolveStatus(
  key: ImportStepKey,
  currentStep: ImportStepKey,
  completed: boolean,
): ImportStepStatus {
  const currentIdx = stepOrderIndex(currentStep)
  const keyIdx = stepOrderIndex(key)
  // Current step is always active; steps after current are never completed.
  if (keyIdx === currentIdx) return 'active'
  if (keyIdx > currentIdx) return 'pending'
  if (completed) return 'completed'
  return 'pending'
}
